'use client'

import { Suspense, type ComponentProps } from 'react'

import NextLink from 'next/link'
import { usePathname } from 'next/navigation'

import { documentPath } from '@/lib/seo/document-path'
import { linkRel, requiresDocumentUrl } from '@/lib/seo/indexing-policy'
import { linkHrefString, resolveLinkHref } from '@/lib/seo/link-url'

import { useIndexingPolicy } from './indexing-provider'

type Props = ComponentProps<typeof NextLink>

const ResolvedLink = ({ href, as, rel, documentUrl, ...props }: Props & { documentUrl?: string }) => {
  const policy = useIndexingPolicy()
  return (
    <NextLink
      {...props}
      href={resolveLinkHref(href, documentUrl)}
      as={as ? resolveLinkHref(as, documentUrl) : as}
      rel={linkRel(linkHrefString(as ?? href), rel, policy, documentUrl)}
    />
  )
}

const RelativeLink = (props: Props) => <ResolvedLink {...props} documentUrl={documentPath(usePathname())} />

// Only document-relative hrefs read route data. Absolute/rooted links keep
// their existing prerender/cache behavior; unknown fallback params suspend
// only this individual link until Next can resolve the request pathname.
const Link = (props: Props) => {
  if (requiresDocumentUrl(linkHrefString(props.href)) || (props.as && requiresDocumentUrl(linkHrefString(props.as)))) {
    return (
      <Suspense fallback={<ResolvedLink {...props} />}>
        <RelativeLink {...props} />
      </Suspense>
    )
  }
  return <ResolvedLink {...props} />
}

export default Link
